import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./ReservationSchedulerNew.css";
import AsyncSelect from "react-select/async";

import * as Tb from "react-icons/tb";
import * as Pi from "react-icons/pi";
import { AppContext } from "../../../context/AppContext";
import Utils from "../../../utils/Utils";
import Select from "react-select";
import { toast } from "react-toastify";
import EditorModal from "../scheduler/EditorModal";
import ConfrimPopup from "./ConfrimPopup";
import { de } from "date-fns/locale";
import { se } from "date-fns/locale";
import { set } from "date-fns";
import { timeToMinutes, to12Hour } from "./SchedulerHelpers";

/* ---------- Helpers ---------- */
const pad = (n) => String(n).padStart(2, "0");
const toDate = (v) => (typeof v === "string" ? new Date(v) : v);
const fmtHM = (dateLike) => {
  const d = toDate(dateLike);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const hourKey = (dateLike) => {
  const d = toDate(dateLike);
  return `${pad(d.getHours())}:00`;
};
const unique = (arr) => Array.from(new Set(arr || []));

/** Parse "YYYY-MM-DD HH:mm:ss" (treat as local) -> Date */
const parseBookingTime = (bookingTime) => {
  if (!bookingTime) return null;
  const isoish = bookingTime.replace(" ", "T");
  const d = new Date(isoish);
  return isNaN(d) ? null : d;
};

/** Local Y-M-D string using en-CA, same as your filter */
const ymdEnCA = (d) => (d ? d.toLocaleDateString("en-CA") : "");

/** Allocation check across shapes */
const isAllocated = (r) => {
  if (!r.tables || r?.tables.length === 0) return;
  const tables = JSON.parse(r?.tables);
  if (Array.isArray(tables)) return tables.length > 0;
  return false;
};

/** Normalise minimal fields needed by the UI */
const normalise = (r) => {
  // Prefer bookingTime like your API; fallback to startISO if present
  const start = r.bookingTime
    ? parseBookingTime(r.bookingTime)
    : r.startISO
    ? new Date(r.startISO)
    : null;
  const startISO = start ? start.toISOString() : null;
  const endISO = start
    ? new Date(start.getTime() + 60 * 60000).toISOString()
    : null; // default 60 mins

  const tableIdFromArray =
    Array.isArray(r.tables) && r.tables.length
      ? r.tables[0]?.id ?? r.tables[0]
      : null;

  return {
    id: String(r.id ?? r._id ?? Math.random()),
    guest: r.name ?? r.guest ?? "Guest",
    phone: r.phone || "",
    partySize: Number(r.chairs ?? r.partySize ?? 1),
    startISO,
    endISO,
    tags: r.tags || [],
    notes: r.message ?? r.notes ?? "",
    tableId: r.tableId ?? tableIdFromArray ?? r.table ?? null,
    tables: r.tables,
    bookingTime: r.bookingTime,
    _raw: r,
  };
};

const timeToMins = (s) => {
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};

export default function UnlistedReservations({
  reservations = [],
  tables = [],
  selectedDate,
  onAssign,
  fetchDateEvents,
  show,
  editing,
  onClose,
  onSave,
  onDelete,
  setEditing,
}) {
  const {
    fetchAvailableTables,
    reservationLoading,
    fetchNewReservations,
    todaysReservationList,
    fetchFutureReservations,
    tokenData,
    updateEnquiry,
    upcomingwithtodaysReservationList,
    tableLists,
    reservFoodpageTiming,
    closureList,
  } = useContext(AppContext);
  const menuRef = useRef(null);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("time");
  const [expanded, setExpanded] = useState(() => new Set());
  const [sectionOpen, setSectionOpen] = useState(true);
  const [menuForId, setMenuForId] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [assigning, setAssigning] = useState({});
  const [availTables, setAvailTables] = useState(null);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [selectedTables, setSelectedTables] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [alert, setAlert] = useState(null);
  const [showConfrim, setShowConfirm] = useState(false);
  const [eventId, setEventId] = useState(null);

  const closeCardMenu = () => {
    setMenuForId(null);
    setMenuPos(null);
  };

  useEffect(() => {
    if (!menuForId) return;
    const onDoc = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (e.target.closest?.(".ur-card__menu-btn")) return;
      closeCardMenu();
    };
    const onRepositionClose = () => closeCardMenu();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    window.addEventListener("scroll", onRepositionClose, true);
    window.addEventListener("resize", onRepositionClose);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      window.removeEventListener("scroll", onRepositionClose, true);
      window.removeEventListener("resize", onRepositionClose);
    };
  }, [menuForId]);

  const openCardMenu = (id, e) => {
    e.stopPropagation();
    if (menuForId === id) {
      closeCardMenu();
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuW = 140;
    const left = Math.min(
      Math.max(8, rect.right - menuW),
      window.innerWidth - menuW - 8
    );
    const top = Math.min(rect.bottom + 4, window.innerHeight - 8);
    setMenuPos({ top, left });
    setMenuForId(id);
  };

  const showAlert = (type, message, timeout = 3000) => {
    setAlert({ type, message });

    setTimeout(() => {
      setAlert(null);
    }, timeout);
  };

  /* Helper for merging date & time */
  const mergeDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const t = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
    return `${dateStr} ${t}`;
  };

  // const loadTableOptions = async (inputValue) => {
  //   const merged = mergeDateTime(
  //     reservationDetails?.bookingDate,
  //     reservationDetails?.bookingTime
  //   );
  //   if (!merged) return [];

  //   try {
  //     setTablesLoading(true);

  //     const res = await new Promise((resolve, reject) => {
  //       fetchAvailableTables(
  //         { bookingTime: merged, q: inputValue || "" },
  //         { onSuccess: resolve, onFailed: reject }
  //       );
  //     });

  //     const list = Array.isArray(res?.data?.available)
  //       ? res.data.available
  //       : [];

  //     const opts = list.map((t) => {
  //       const rawId = t.id;
  //       const value = toNumIfNumeric(rawId);
  //       return {
  //         value, // numeric when possible
  //         label: `${t.name || t.label || "Table"}${
  //           t.chairs ? `  (${t.chairs})` : ""
  //         }`,
  //         meta: t,
  //       };
  //     });

  //     setTableOptionsCache(opts);

  //     if (!inputValue) return opts;
  //     const q = inputValue.toLowerCase();
  //     return opts.filter((o) => o.label.toLowerCase().includes(q));
  //   } catch (e) {
  //     console.error("fetchAvailableTables failed", e);
  //     return [];
  //   } finally {
  //     setTablesLoading(false);
  //   }
  // };

  // const handleTablesMenuOpen = async () => {
  //   if (tableOptionsCache.length === 0) {
  //     await loadTableOptions("");
  //   }
  // };

  /* --- Steps for fetching un allocated reservations --- */
  /* 1. Normalise */
  const src = useMemo(() => {
    const list = Array.isArray(reservations) ? reservations : [];
    return list.map(normalise).filter((r) => r.startISO);
  }, [reservations]);

  /* 2. Filter by selectedDate */
  const dateFiltered = useMemo(() => {
    if (!selectedDate) return src; // if not provided, show all
    return src.filter((r) => ymdEnCA(new Date(r.startISO)) === selectedDate);
  }, [src, selectedDate]);

  /* 3. Only unallocated */
  const unallocated = useMemo(
    () => dateFiltered.filter((r) => !isAllocated(r)),
    [dateFiltered]
  );

  /* 4. Text filter (name/phone/tag) */
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return unallocated;

    return unallocated.filter((r) => {
      const hay = `${r.guest || ""} ${r.phone || ""} ${
        r?._raw?.status || ""
      } ${(r.tags || []).join(" ")}`.toLowerCase();

      return hay.includes(term);
    });
  }, [q, unallocated]);

  /* 5. Sort */
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const cmp =
      sortBy === "party"
        ? (a, b) => (b.partySize || 0) - (a.partySize || 0)
        : sortBy === "name"
        ? (a, b) => (a.guest || "").localeCompare(b.guest || "")
        : (a, b) => new Date(a.startISO) - new Date(b.startISO); // time
    return arr.sort(cmp);
  }, [filtered, sortBy]);

  /* 6. Group by hour */
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of sorted) {
      const key = hourKey(r.startISO);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return map;
  }, [sorted]);

  const hourKeys = useMemo(
    () => unique(sorted.map((r) => hourKey(r.startISO))),
    [sorted]
  );

  /* Keep all open by default when keys change */
  useEffect(() => {
    setExpanded(new Set(hourKeys));
  }, [hourKeys]);

  const allOpen = hourKeys.length > 0 && expanded.size === hourKeys.length;

  const toggleAll = () => {
    setExpanded(
      (prev) =>
        hourKeys.length > 0 && prev.size === hourKeys.length
          ? new Set() // collapse all
          : new Set(hourKeys) // expand all
    );
  };

  const collapseAll = () => setExpanded(new Set());
  const expandAll = () => setExpanded(new Set(hourKeys));
  const toggleHour = (k) => {
    const next = new Set(expanded);
    next.has(k) ? next.delete(k) : next.add(k);
    setExpanded(next);
  };

  /* Assign handlers */
  const setAssignChoice = (id, tableId) =>
    setAssigning((p) => ({ ...p, [id]: tableId }));
  const handleAssign = (id) => {
    const tableId = assigning[id];
    if (!tableId) return;
    onAssign?.(id, { tableId });
    setAssigning((p) => {
      const copy = { ...p };
      delete copy[id];
      return copy;
    });
  };

  const cancelAssign = () => {
    setAvailTables(null);
    setSelectedSchedule(null);
    setSelectedTables(null);
    setAlert(null);
  };

  const fetchAvailTables = async (data) => {
    try {
      setTablesLoading(true);
      setAvailTables([]);
      setSelectedTables(null);
      setSelectedSchedule(data);

      const payload = {
        bookingTime: data?.bookingTime,
      };

      await fetchAvailableTables(payload, {
        onSuccess: (res) => {
          const resp = res?.data;
          if (resp && resp.available) {
            const data = resp.available;
            if (data && data.length == 0) {
              toast.error("Tables are full in this time slot!");
            }
            const alteredData = data.map((d) => {
              return {
                value: d?.id,
                label: `${d?.name || "Table"}${
                  d?.chairs ? `  (${d.chairs})` : ""
                }`,
                meta: {
                  id: d?.id,
                  shopID: d?.shopID,
                  tableID: d?.tableID,
                  name: d?.name,
                  chairs: d?.chairs,
                  status: d?.status,
                  tableType: d?.tableType,
                },
              };
            });

            setAvailTables(alteredData);
          }
        },
        onFailed: (err) => {
          console.error("Error on nfetching data", err);
        },
      });
    } finally {
      setTablesLoading(false);
    }
  };

  const validateChairsandPeoples = () => {
    if (
      !selectedTables ||
      selectedTables.length == 0 ||
      !selectedSchedule?.partySize ||
      selectedSchedule?.partySize == 0
    )
      return;

    const totalChairs = selectedTables.reduce(
      (sum, o) => sum + (parseInt(o.meta?.chairs, 10) || 0),
      0
    );

    // if (Number(selectedSchedule?.partySize) < totalChairs) {
    //   showAlert(
    //     "warning",
    //     "The selected table(s) are too large for the number of people"
    //   );
    //   return true;
    // }

    if (Number(selectedSchedule?.partySize) > totalChairs) {
      showAlert(
        "danger",
        "The selected table(s) are too small for the number of people"
      );

      return true;
    } else {
      setAlert(null);
      return false;
    }
  };

  const isReservationWithinOpenTime = (date, time) => {
    // basic input validation
    if (!reservFoodpageTiming || !date || !time) {
      return {
        isValid: false,
        errorText: "Booking date and time are required.",
      };
    }

    const bookingDateObj = new Date(date);
    if (isNaN(bookingDateObj.getTime())) {
      console.error("Invalid bookingDate:", date);
      return {
        isValid: false,
        errorText: "Invalid booking date.",
      };
    }

    // build full booking datetime
    const timeStr = time;
    const normalisedTime = timeStr?.length === 5 ? `${timeStr}:00` : timeStr;

    const bookingDateTime = new Date(
      `${date} ${normalisedTime}`.replace(" ", "T")
    );
    if (isNaN(bookingDateTime.getTime())) {
      console.error("Invalid booking datetime:", date, time);
      return {
        isValid: false,
        errorText: "Invalid booking time.",
      };
    }

    // 🔹 First: check closureList (if any) – assumed from outer scope
    if (Array.isArray(closureList) && closureList.length > 0) {
      const matchingClosure = closureList.find((c) => {
        if (c.status !== "Active") return false;

        const start = new Date(c.startTime.replace(" ", "T"));
        const end = new Date(c.endTime.replace(" ", "T"));

        if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;

        return bookingDateTime >= start && bookingDateTime <= end;
      });

      if (matchingClosure) {
        const startTime24 = matchingClosure.startTime.slice(11, 16);
        const endTime24 = matchingClosure.endTime.slice(11, 16);
        const startTime12 = to12Hour(startTime24);
        const endTime12 = to12Hour(endTime24);
        const dateStr = matchingClosure.startTime.slice(0, 10);

        return {
          isValid: false,
          errorText: `Closed on ${dateStr} from ${startTime12} to ${endTime12}.`,
        };
      }
    }

    // 🔹 Then: validate against regular opening times
    const dayNames = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const dayKey = dayNames[bookingDateObj.getDay()];

    const daySlots = reservFoodpageTiming[dayKey];
    if (!Array.isArray(daySlots) || daySlots.length === 0) {
      return {
        isValid: false,
        errorText: "Shop is closed on the selected day.",
      };
    }

    const bookingMinutes = timeToMinutes(time);
    if (bookingMinutes == null) {
      console.error("Invalid bookingTime:", time);
      return {
        isValid: false,
        errorText: "Invalid booking time.",
      };
    }

    const isWithinOpenTime = daySlots
      .filter((slot) => slot.status === "active")
      .some((slot) => {
        const openMin = timeToMinutes(slot.openingTime);
        const closeMin = timeToMinutes(slot.closingTime);
        if (openMin == null || closeMin == null) return false;

        return bookingMinutes >= openMin && bookingMinutes <= closeMin;
      });

    if (!isWithinOpenTime) {
      return {
        isValid: false,
        errorText: "Selected time is outside opening hours.",
      };
    }

    return {
      isValid: true,
      errorText: null,
    };
  };

  const handleUpdate = async (e) => {
    try {
      setTablesLoading(true);
      e.preventDefault();

      if (!selectedTables || (selectedTables && selectedTables.length == 0)) {
        showAlert("danger", "Please select table(s) to assign.");
        return;
      }

      const validateTablesAndChairs = validateChairsandPeoples();

      if (validateTablesAndChairs) return;
      const [date, time] = selectedSchedule?.bookingTime?.split(" ");
      const siteStorageURL = localStorage.getItem("siteURL") || "";
      const siteURL = siteStorageURL
        ? `${siteStorageURL}/view-reservation?reserv=`
        : "";

      const tableIds = [
        ...new Set((selectedTables ?? []).map((t) => Number(t?.value))),
      ];

      const updatePayload = {
        tables: tableIds,
        bookingDate: date,
        bookingTime: time,
        chairs: Number(selectedSchedule?.partySize),
        userID: 0,
        shopmessage: "",
        advancePayment: "",
        advanceAmount: "",
        amountStatus: "",
        paymentMethod: "",
        transactionID: "",
        baseUrl: siteURL,
        source: "NextJs",
      };

      const shopToken = tokenData?.shopCustomToken;
      if (!shopToken) return showAlert("error", "Authorisation token missing.");
      const headers = { "x-shopToken": shopToken };

      await updateEnquiry?.(selectedSchedule?.id, updatePayload, {
        onSuccess: async () => {
          showAlert("success", "Reservation details updated successfully.");
          await fetchNewReservations?.(shopToken);
          await todaysReservationList?.(shopToken);
          await fetchFutureReservations?.(shopToken);
          await upcomingwithtodaysReservationList();
          cancelAssign();
          setTimeout(() => {
            showAlert(null);
          }, 1000);
        },
        onFailed: (err) => {
          debugger;
          console.log(err, "error on updating");
          showAlert(
            "danger",
            err?.messages || "Update failed. Please try again."
          );
        },
        headers,
      });
    } finally {
      setTablesLoading(false);
    }
  };

  const handleOpenModal = (data) => {
    if (!data) return;

    const [datePart, timePartFull] = String(data.bookingTime || "").split(" ");
    const timePart = (timePartFull || "").slice(0, 5);

    const start = timeToMins(timePart);
    const end = start + 120;

    const alteredData = {
      id: data?._raw?.id,
      formattedID: data?._raw?.formattedID ?? null,
      tableId: tableLists && tableLists.length != 0 ? tableLists[0]?.id : 0,
      date: datePart,
      start,
      end,
      name: data?.guest,
      chairs: data?.partySize ?? 2,
      bookingTime: timePart,
      bookingDate: datePart,
      email: data?._raw?.email,
      phone: data?._raw?.phone,
      status: data?._raw?.status || "requested",
    };

    setEditing(alteredData);
  };

  const handleDeleteModal = (data) => {
    if (!data) return;

    setEventId(data?._raw?.id);
    window.sessionStorage.setItem("eventID", data?._raw?.id);
    setShowConfirm(true);
    closeCardMenu();
  };
  const onCloseConfirm = () => {
    setShowConfirm(false);
    setEventId(null);
  };

  return (
    <>
      {showConfrim && (
        <ConfrimPopup
          close={onCloseConfirm}
          eventId={eventId}
          setEventId={setEventId}
          showConfrim={showConfrim}
          setShowConfirm={setShowConfirm}
        />
      )}
      <section
        className="ur-panel"
        aria-label="Unallocated reservations"
      >
        {/* Header controls */}
        <div className="ur-toolbar border-none">
          <div className="ur-toolbar__row ur-toolbar__row--head">
            <div className="ur-title">
              Unallocated
              <span className="ur-count">• {unallocated.length || 0}</span>
            </div>

            {sectionOpen && (
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="ur-input ur-input--toolbar"
                placeholder="Search name, phone, tag…"
                aria-label="Search unallocated reservations"
              />
            )}

            <div className="ur-toolbar__controls">
              {/* Sort as icon; native select still opens options */}
              <div
                className="ur-sort"
                title={
                  sortBy === "party"
                    ? "Sort by party size"
                    : sortBy === "name"
                      ? "Sort by name"
                      : "Sort by time"
                }
              >
                <Tb.TbArrowsSort className="ur-sort__icon" aria-hidden />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="ur-select ur-select--icon"
                  aria-label="Sort by"
                >
                  <option value="time">Sort by time</option>
                  <option value="party">Sort by party size</option>
                  <option value="name">Sort by name</option>
                </select>
              </div>

              <button
                type="button"
                className="collapse___btn"
                onClick={() => setSectionOpen((o) => !o)}
                aria-expanded={sectionOpen}
                aria-label={
                  sectionOpen
                    ? "Collapse unallocated section"
                    : "Expand unallocated section"
                }
                title={sectionOpen ? "Collapse" : "Expand"}
              >
                {sectionOpen ? <Tb.TbChevronUp /> : <Tb.TbChevronDown />}
              </button>
            </div>
          </div>
        </div>

        {sectionOpen && (
          <>
            {(unallocated.length || 0) === 0 ? (
              <div className="ur-empty">
                <div className="ur-empty__text">
                  {selectedDate
                    ? `No unallocated bookings for ${Utils.formatDate(
                        selectedDate
                      )}`
                    : "All reservations are allocated"}
                </div>
              </div>
            ) : (
              <>
                <div className="ur-list" role="list">
                  {sorted.map((r) => {
                    const currentStatus =
                      unallocated.find((item) => item.id === r.id)?._raw
                        ?.status || "";
                    const canAssign =
                      currentStatus !== "Cancelled" &&
                      currentStatus !== "Rejected";
                    const isAssigningThis =
                      String(selectedSchedule?.id ?? "") === String(r.id);

                    return (
                      <div
                        className={`ur-card-wrap${
                          isAssigningThis ? " is-assigning" : ""
                        }`}
                        key={r.id}
                      >
                        <article
                          className={`ur-card ur-card--row${
                            isAssigningThis ? " ur-card--assigning" : ""
                          }`}
                          role="listitem"
                          onClick={() => {
                            if (!reservationLoading && !isAssigningThis)
                              handleOpenModal(r);
                          }}
                        >
                          <div className="ur-card__main">
                            <div className="ur-card__info">
                              <div className="ur-card__top">
                                <div className="ur-card__name">
                                  {r.guest || "Guest"}
                                </div>
                                <span className="ur-badge ur-badge--guests">
                                  <Pi.PiUserDuotone aria-hidden />
                                  {r.partySize || 1}
                                </span>
                              </div>
                              <div className="ur-card__meta">
                                <span className="ur-meta__time">
                                  {fmtHM(r.startISO)} – {fmtHM(r.endISO)}
                                </span>
                              </div>
                            </div>

                            <div
                              className="ur-card__actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {canAssign ? (
                                !isAssigningThis && (
                                  <button
                                    type="button"
                                    className="ur-btn ur-btn--assign-outline"
                                    onClick={() => {
                                      fetchAvailTables(r);
                                      setAlert(null);
                                    }}
                                    disabled={
                                      tablesLoading &&
                                      String(selectedSchedule?.id ?? "") ===
                                        String(r.id)
                                    }
                                  >
                                    {tablesLoading &&
                                    String(selectedSchedule?.id ?? "") ===
                                      String(r.id)
                                      ? "Loading…"
                                      : "Assign"}
                                  </button>
                                )
                              ) : (
                                <span className="ur-card__blocked">
                                  {currentStatus}
                                </span>
                              )}

                              {isAssigningThis && (
                                <button
                                  type="button"
                                  className="ur-assign-close refresh_btn"
                                  onClick={cancelAssign}
                                  disabled={reservationLoading}
                                  aria-label="Cancel assign"
                                  title="Cancel"
                                >
                                  <Tb.TbX />
                                </button>
                              )}

                              <div className="ur-card__menu">
                                <button
                                  type="button"
                                  className="ur-card__menu-btn"
                                  aria-label="More actions"
                                  aria-expanded={menuForId === r.id}
                                  disabled={reservationLoading}
                                  onClick={(e) => openCardMenu(r.id, e)}
                                >
                                  <Pi.PiDotsThreeBold />
                                </button>
                              </div>
                            </div>
                          </div>
                        </article>

                        {isAssigningThis && (
                          <form
                            className="ur-card__assign-panel"
                            onSubmit={handleUpdate}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="ur-card__assign-field">
                              <label
                                className="ur-card__assign-label"
                                htmlFor={`ur-assign-${r.id}`}
                              >
                                Select table(s)
                              </label>
                              <Select
                                inputId={`ur-assign-${r.id}`}
                                options={availTables || []}
                                isLoading={tablesLoading}
                                isDisabled={tablesLoading}
                                menuPortalTarget={
                                  typeof document !== "undefined"
                                    ? document.body
                                    : null
                                }
                                menuPosition="fixed"
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: 40,
                                    borderRadius: 10,
                                    borderColor: "#e5e7eb",
                                    boxShadow: "none",
                                  }),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                }}
                                isMulti
                                menuPlacement="auto"
                                menuShouldScrollIntoView={false}
                                onChange={(e) => setSelectedTables(e)}
                                value={selectedTables}
                                placeholder={
                                  tablesLoading
                                    ? "Loading tables…"
                                    : "Choose table(s)"
                                }
                              />
                            </div>
                            <div className="ur-card__assign-actions">
                              <button
                                className="ur-btn ur-btn--assign"
                                type="submit"
                                disabled={
                                  tablesLoading ||
                                  reservationLoading ||
                                  !selectedTables ||
                                  selectedTables.length === 0
                                }
                              >
                                Confirm
                              </button>
                            </div>
                          </form>
                        )}

                        {alert?.message &&
                          selectedSchedule?.id == r?.id && (
                            <div
                              className={`alert alert-${alert?.type}`}
                              role="alert"
                            >
                              {alert?.message ?? ""}
                              <button
                                type="button"
                                className="rbm-alert__close"
                                aria-label="Dismiss"
                                onClick={() => setAlert(null)}
                              >
                                ×
                              </button>
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {editing && (
          <EditorModal
            show={show}
            payload={editing}
            onClose={onClose}
            onSave={onSave}
            onDelete={onDelete}
            fetchDateEvents={fetchDateEvents}
          />
        )}
      </section>

      {menuForId &&
        menuPos &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="ur-card__menu-pop ur-card__menu-pop--portal"
            ref={menuRef}
            role="menu"
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              zIndex: 10050,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="ur-card__menu-item ur-card__menu-item--danger"
              role="menuitem"
              disabled={reservationLoading}
              onClick={() => {
                const item = sorted.find(
                  (x) => String(x.id) === String(menuForId)
                );
                if (item) handleDeleteModal(item);
              }}
            >
              <Pi.PiTrash /> Delete
            </button>
          </div>,
          document.body
        )}
    </>
  );
}
